// The shared partner product is separate from the authenticated child-game ID.
export const MJH5_LAUNCH = {
  command: "partner_enter_game",
  gameUid: "60001",
  platform: "sakaddz",
};

const makeGame = (key, name, id) => Object.freeze({
  key, name, targetGameId: id, iconFile: `BoardCard_${name}.png`,
  matchEntry: Object.freeze({ target_game_id: id, entry_page: "match" }),
  createEntry: Object.freeze({ target_game_id: id, entry_page: "create_room" }),
});
const GAMES = Object.freeze({
  49900: makeGame("qiuqiu", "QiuQiu", 49900),
  13200: makeGame("landlord", "Landlord", 13200),
});

// Only these older saved selections can be migrated to the new contract.
export const MJH5_ENTRIES = ["lobby", "landlord", "qiuqiu", "qiuqiu_create_room"];
const LEGACY_ENTRIES = Object.freeze({
  landlord: GAMES[13200].matchEntry,
  qiuqiu: GAMES[49900].matchEntry,
  qiuqiu_create_room: GAMES[49900].createEntry,
});
const owns = (object, key) => Object.prototype.hasOwnProperty.call(object, key);
const readGameId = value => {
  if (typeof value !== "number" && typeof value !== "string") return null;
  if (!/^[1-9]\d*$/.test(String(value))) return null;
  const id = Number(value);
  return Number.isSafeInteger(id) && owns(GAMES, id) ? id : null;
};

export function normalizeMjh5Entry(entry) {
  if (entry == null || entry === "lobby") return "lobby";
  if (typeof entry === "string") return owns(LEGACY_ENTRIES, entry) ? LEGACY_ENTRIES[entry] : null;
  if (typeof entry !== "object" || Array.isArray(entry) ||
      Object.keys(entry).some(key => !["target_game_id", "entry_page"].includes(key))) return null;
  const id = readGameId(entry.target_game_id);
  if (!id) return null;
  if (entry.entry_page === "match") return GAMES[id].matchEntry;
  if (entry.entry_page === "create_room") return GAMES[id].createEntry;
  return null;
}

export function getMjh5LaunchParams(entry = "lobby") {
  const target = normalizeMjh5Entry(entry);
  if (!target) throw new Error("INVALID_DDZ_ENTRY");
  return target === "lobby" ? {} : { ...target };
}

export function isMjh5Game(game) {
  if (!game) return false;
  return game.game_uid === MJH5_LAUNCH.gameUid ||
    (typeof game.game_uid === "number" && String(game.game_uid) === MJH5_LAUNCH.gameUid) ||
    game.platfrom === MJH5_LAUNCH.platform;
}

export function isBoardCardDdzEntry(game) {
  return !!game && !game.isLogo &&
    String(game.key || "").replace(/\s/g, "") === "BoardCard" &&
    String(game.name || "").replace(/[\s']/g, "") === "BoardCard";
}

export function isMjh5CategoryEntry(game) {
  if (!game || game.isLogo) return false;
  const boardCard = String(game.key || "").replace(/\s/g, "") === "BoardCard";
  if (!boardCard && !isMjh5Game(game)) return false;
  const name = String(game.name || "").replace(/[\s']/g, "").toLowerCase();
  return owns(game, "target_game_id") || isBoardCardDdzEntry(game) || ["qiuqiu", "landlord"].includes(name);
}

function categoryDefinition(game) {
  if (!isMjh5CategoryEntry(game)) return null;
  // Explicit IDs always win. An unsupported ID must never fall back by name.
  if (owns(game, "target_game_id")) {
    const id = readGameId(game.target_game_id);
    return id ? GAMES[id] : null;
  }
  if (isBoardCardDdzEntry(game)) return GAMES[49900];
  const name = String(game.name || "").replace(/[\s']/g, "").toLowerCase();
  return name === "qiuqiu" ? GAMES[49900] : name === "landlord" ? GAMES[13200] : null;
}

export function getMjh5CategoryGame(game) {
  const definition = categoryDefinition(game);
  if (!definition || game.coming || [false, 0, "0"].includes(game.enabled)) return null;
  const pages = owns(game, "entry_pages") ? game.entry_pages : ["match", "create_room"];
  if (!Array.isArray(pages)) return null;
  const matchEntry = pages.includes("match") ? definition.matchEntry : null;
  const createEntry = pages.includes("create_room") ? definition.createEntry : null;
  return matchEntry || createEntry ? { ...definition, matchEntry, createEntry } : null;
}

export function getMjh5CategoryAppearance(game) {
  const definition = categoryDefinition(game);
  return definition ? { name: definition.name, iconFile: definition.iconFile } : null;
}
